import { isAndroid, isIOS, isNative } from './is-native';
import { navigationQuery } from '@/lib/travel/postcode';

/**
 * Every "open this address in maps" in Mucka goes through here.
 *
 * The bug this closes (an owner at a plumbing customer): six screens hand-rolled a
 * `https://maps.apple.com/?q=…` href. On Android that URL has no app to claim
 * it, so Capacitor's `ACTION_VIEW` lands it in the browser and the engineer
 * gets the Apple Maps *website* instead of directions. Nothing was
 * platform-aware except `openDirections`, and even that opened the URL in an
 * in-app browser sheet (SFSafariViewController / Chrome Custom Tabs) which
 * never hands a universal link off to the native app.
 *
 * How a URL actually reaches the maps app: a plain top-level navigation.
 * Capacitor's WebView delegate sees a URL outside `server.url`, cancels the
 * navigation and calls `UIApplication.shared.open` (iOS) or fires an
 * `Intent.ACTION_VIEW` (Android) — which is exactly the OS hand-off we want,
 * universal links and all. `Browser.open()` is the wrong primitive here: it
 * keeps the URL inside the in-app browser by design.
 *
 * Platform behaviour:
 *   • Android → `geo:` by default. Every maps app registers for it (Google
 *     Maps, Waze, Organic Maps, HERE), so Android's own default-app setting
 *     decides where it lands, and users with no default get the OS chooser.
 *     This is the AC — respect the OS setting, don't reinvent it.
 *   • iOS → no OS-level "default maps app" exists, so the user picks one once
 *     (Apple Maps / Google Maps / Waze) and we remember it. Plenty of trades
 *     drivers live in Waze.
 *   • Web → Google Maps in a new tab; `geo:` on Android browsers.
 *
 * Every URL is an https universal link (bar `geo:`, and bar the iOS scheme
 * hand-off in `schemeUrl` below), so there is no `LSApplicationQueriesSchemes`
 * / `<queries>` manifest work and no new Capacitor plugin — this ships to the
 * hosted WebView instantly rather than waiting on a store release.
 */

export type NavApp = 'system' | 'apple' | 'google' | 'waze';

const PREF_KEY = 'mucka_nav_app';

const ALL: readonly NavApp[] = ['system', 'apple', 'google', 'waze'];

function isNavApp(value: unknown): value is NavApp {
  return typeof value === 'string' && (ALL as readonly string[]).includes(value);
}

/**
 * Which family of maps URLs this device can actually honour. Deliberately
 * separate from `platform()`: the Android PWA and Android Chrome both report
 * `web`, and they were hitting the Apple Maps website too. A default is not a
 * security boundary, so light UA sniffing is the right call here.
 */
function deviceKind(): 'android' | 'ios' | 'other' {
  if (isNative()) {
    if (isAndroid()) return 'android';
    if (isIOS()) return 'ios';
    return 'other';
  }
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'other';
}

/** What we use when the user has never picked. */
export function defaultNavApp(): NavApp {
  switch (deviceKind()) {
    case 'android': return 'system';
    case 'ios': return 'apple';
    default: return 'google';
  }
}

/**
 * Whether asking the user to pick is worth it. On Android the OS already owns
 * this choice, so a Mucka chooser on top of Android's chooser is noise.
 */
export function shouldOfferNavAppChoice(): boolean {
  return deviceKind() === 'ios';
}

/** The stored preference, or null if the user has never picked one. */
export function getNavApp(): NavApp | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PREF_KEY);
    return isNavApp(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Anything rendering a maps href subscribes here, so changing the app in
 * Settings re-points address rows already on screen instead of leaving them
 * pointing at the old app until the next navigation.
 */
const listeners = new Set<() => void>();

export function subscribeNavApp(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Persist the user's pick. Pass null to go back to the platform default. */
export function setNavApp(app: NavApp | null): void {
  try {
    if (typeof localStorage !== 'undefined') {
      if (app === null) localStorage.removeItem(PREF_KEY);
      else localStorage.setItem(PREF_KEY, app);
    }
  } catch {
    /* private mode / storage full — fall back to the platform default */
  }
  listeners.forEach(cb => cb());
}

/**
 * Coerce a preference into something the device can honour. Guards the exact
 * fault we're fixing: `apple` must never survive onto an Android device, and
 * `system` (a `geo:` URL) does nothing on iOS or desktop.
 */
function coerce(app: NavApp): NavApp {
  const kind = deviceKind();
  if (kind === 'android') return app === 'apple' ? 'system' : app;
  if (app === 'system') return kind === 'ios' ? 'apple' : 'google';
  if (app === 'apple' && kind !== 'ios') return 'google';
  return app;
}

/** The app we'd open right now — stored preference, sanity-checked, else default. */
export function resolveNavApp(): NavApp {
  const stored = getNavApp();
  return coerce(stored ?? defaultNavApp());
}

/** "12 Elm Rd" + "SW1A 1AA" → "12 Elm Rd, SW1A 1AA". Either part may be blank. */
export function joinAddress(address?: string | null, postcode?: string | null): string {
  return [address, postcode].filter(Boolean).join(', ');
}

/**
 * Where to send the maps app: free text it geocodes itself, or a coordinate.
 *
 * A coordinate is a property's DOOR PIN (TKT-1445, `src/lib/properties/
 * access-pin.ts`) — the spot an engineer actually stood at. It beats the
 * address on any rural job, where a postcode can cover several farms. The
 * `label` is only what the maps app shows on the pin; navigation goes to the
 * coordinate.
 */
export type MapsDestination =
  | string
  | { lat: number; lng: number; label?: string | null }
  | WordsDestination;

/**
 * A door pin that is three words and nothing else (TKT-1445): one set from a
 * customer's what3words. We hold no coordinate for it — what3words' licence
 * (clause 6.3(e)) forbids storing theirs beside the words — so `openDirections`
 * asks `/api/what3words/locate` at the tap and hands the answer straight to the
 * maps app. Where only a synchronous URL will do (an `<a href>`), it is
 * what3words' own page for the words, which can navigate from there.
 */
export interface WordsDestination { words: string; label?: string | null }

function isCoord(d: MapsDestination): d is { lat: number; lng: number; label?: string | null } {
  return typeof d === 'object' && d !== null && 'lat' in d && Number.isFinite(d.lat) && Number.isFinite(d.lng);
}

export function isWordsDestination(d: MapsDestination): d is WordsDestination {
  return typeof d === 'object' && d !== null && 'words' in d && typeof d.words === 'string' && Boolean(d.words);
}

/** what3words' own page for three words: a map with a Navigate button. Costs no API call. */
export function threeWordsUrl(words: string): string {
  return `https://w3w.co/${encodeURIComponent(words)}`;
}

/** "///filled.count.soap" — how a three-word pin travels as ONE string (see `readDestination`). */
const WORDS_STRING = /^\s*\/\/\/(\p{L}+\.\p{L}+\.\p{L}+)\s*$/u;

const COORD_STRING = /^\s*(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)\s*$/;

/**
 * "52.860123,-3.050457" as a coordinate. The AI's `open_directions` hands its
 * destination over as ONE STRING (`navAction.directionsTo`), and that string
 * travels through voice events, the chat journal and stored transcripts; a
 * door pin rides along in this form rather than widening all of them. Needs a
 * decimal point on both halves, so no street address can be mistaken for one.
 */
function readDestination(d: MapsDestination): MapsDestination {
  if (typeof d !== 'string') return d;
  const w = WORDS_STRING.exec(d);
  if (w) return { words: w[1].toLowerCase() };
  const m = COORD_STRING.exec(d);
  if (m) {
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
  }
  // An address goes as "number street, postcode", or the postcode alone —
  // never a house name, town or county, which let the maps app's search
  // outvote the postcode and send the engineer to a different house. Story
  // and measurements: navigationQuery.
  return navigationQuery(d);
}

/** "52.860123,-3.050456" — six places is ~10cm, plenty for a doorstep. */
function ll(d: { lat: number; lng: number }): string {
  return `${d.lat.toFixed(6)},${d.lng.toFixed(6)}`;
}

/** True when there is nothing to navigate to. */
function isEmptyDestination(d: MapsDestination): boolean {
  return isCoord(d) || isWordsDestination(d) ? false : !(typeof d === 'string' && d.trim());
}

/** Turn-by-turn directions to a destination. */
export function directionsUrl(app: NavApp, rawDestination: MapsDestination): string {
  const destination = readDestination(rawDestination);
  if (isWordsDestination(destination)) return threeWordsUrl(destination.words);
  if (isCoord(destination)) {
    const c = ll(destination);
    const label = encodeURIComponent((destination.label || '').trim());
    switch (coerce(app)) {
      case 'system': return label ? `geo:${c}?q=${c}(${label})` : `geo:${c}?q=${c}`;
      case 'waze': return `https://waze.com/ul?ll=${c}&navigate=yes`;
      case 'google': return `https://www.google.com/maps/dir/?api=1&destination=${c}&travelmode=driving`;
      case 'apple':
      default: return `https://maps.apple.com/?daddr=${c}&dirflg=d`;
    }
  }
  const q = encodeURIComponent(destination.trim());
  switch (coerce(app)) {
    // `geo:` has no "start navigating" flag every app agrees on, so this drops
    // the user on the destination in their own maps app, one tap from Go.
    case 'system': return `geo:0,0?q=${q}`;
    case 'waze': return `https://waze.com/ul?q=${q}&navigate=yes`;
    case 'google': return `https://www.google.com/maps/dir/?api=1&destination=${q}&travelmode=driving`;
    case 'apple':
    default: return `https://maps.apple.com/?daddr=${q}&dirflg=d`;
  }
}

/** Drop a pin on a place without starting navigation — what an address row does. */
export function placeUrl(app: NavApp, rawPlace: MapsDestination): string {
  const place = readDestination(rawPlace);
  if (isWordsDestination(place)) return threeWordsUrl(place.words);
  if (isCoord(place)) {
    const c = ll(place);
    const label = encodeURIComponent((place.label || '').trim());
    switch (coerce(app)) {
      case 'system': return label ? `geo:${c}?q=${c}(${label})` : `geo:${c}?q=${c}`;
      case 'waze': return `https://waze.com/ul?ll=${c}`;
      case 'google': return `https://www.google.com/maps/search/?api=1&query=${c}`;
      case 'apple':
      default: return label ? `https://maps.apple.com/?ll=${c}&q=${label}` : `https://maps.apple.com/?ll=${c}`;
    }
  }
  const q = encodeURIComponent(place.trim());
  switch (coerce(app)) {
    case 'system': return `geo:0,0?q=${q}`;
    case 'waze': return `https://waze.com/ul?q=${q}`;
    case 'google': return `https://www.google.com/maps/search/?api=1&query=${q}`;
    case 'apple':
    default: return `https://maps.apple.com/?q=${q}`;
  }
}

/** href for an `<a>` that shows a place in the user's maps app. */
export function placeHref(place: MapsDestination): string {
  return placeUrl(resolveNavApp(), place);
}

/** href for an `<a>` that starts directions in the user's maps app. */
export function directionsHref(destination: MapsDestination): string {
  return directionsUrl(resolveNavApp(), destination);
}

/**
 * The maps app's OWN url scheme, for the one platform where the https link is
 * not enough. Null everywhere else, which is the normal case.
 *
 * The bug this closes (an engineer at a heating customer, TKT-1255): Waze set as his app in
 * Settings, and tapping Directions did not open it. Every URL above is an
 * https universal link, and on iOS a universal link reaches the app only if
 * the OS agrees to resolve it — which it does not reliably. It has broken
 * outright across iOS releases (13, 16.5, 17), and, worse for us, iOS
 * REMEMBERS a per-domain "open in the browser" choice the moment somebody taps
 * the Safari breadcrumb on one. That is a one-way door the user cannot find
 * again, and it is exactly what "it was working fine yesterday" looks like.
 *
 * Google's own Waze deep-link documentation says as much: on iOS, open
 * `waze://` when the app is there and keep `https://waze.com/ul` as the WEB
 * fallback. So that is the order here — scheme first, https if the OS does not
 * take it.
 *
 * Apple Maps is deliberately absent: `maps.apple.com` is special-cased by iOS
 * itself and has never needed this. `system` is a `geo:` URL and never survives
 * `coerce` onto iOS at all.
 *
 * No `LSApplicationQueriesSchemes` work: that plist key gates `canOpenURL`,
 * which we never call. Capacitor cancels the navigation and hands the URL to
 * `UIApplication.open`, which is not gated by it.
 */
function schemeUrl(app: NavApp, rawDestination: MapsDestination, mode: 'place' | 'directions'): string | null {
  const destination = readDestination(rawDestination);
  // Native only. In iOS Safari the tap is a real link activation, which is the
  // one case universal links are reliable for, and the timed fallback below
  // has no way to tell a backgrounded tab from an ignored scheme.
  if (!isNative() || deviceKind() !== 'ios') return null;
  if (isWordsDestination(destination)) return null;
  if (isCoord(destination)) {
    const c = ll(destination);
    switch (app) {
      case 'waze':
        return mode === 'directions' ? `waze://?ll=${c}&navigate=yes` : `waze://?ll=${c}`;
      case 'google':
        return mode === 'directions'
          ? `comgooglemaps://?daddr=${c}&directionsmode=driving`
          : `comgooglemaps://?q=${c}`;
      default:
        return null;
    }
  }
  const q = encodeURIComponent(destination.trim());
  switch (app) {
    case 'waze':
      return mode === 'directions' ? `waze://?q=${q}&navigate=yes` : `waze://?q=${q}`;
    case 'google':
      return mode === 'directions'
        ? `comgooglemaps://?daddr=${q}&directionsmode=driving`
        : `comgooglemaps://?q=${q}`;
    default:
      return null;
  }
}

/**
 * Whether a tap on this device needs to go through `openDirections`/`openPlace`
 * rather than letting an `<a href>` navigate on its own. <MapLink> asks,
 * because its href stays the https URL so long-press and copy still give
 * something a person can use.
 */
export function needsSchemeHandoff(app: NavApp = resolveNavApp()): boolean {
  return schemeUrl(coerce(app), 'x', 'directions') !== null;
}

/**
 * How long to give the OS to take a custom scheme before falling back to the
 * web URL. An app that is installed comes to the foreground in well under this;
 * an unknown scheme fails SILENTLY, with no error and no callback, so a timer
 * is the only signal there is.
 */
const SCHEME_HANDOFF_MS = 1200;

/**
 * Try the app's own scheme, and fall back to the https link if nothing takes
 * it — which is what happens when the chosen app simply isn't installed.
 *
 * The page going hidden IS the success signal: the maps app coming to the
 * foreground backgrounds the WebView. `document.hidden` is re-read at fire
 * time as well as watched, so a missed event still cannot send somebody to the
 * website after their maps app already opened.
 */
function launchWithFallback(scheme: string, web: string): void {
  let settled = false;
  let timer = 0;

  const stop = () => {
    settled = true;
    window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', stop);
  };
  function onVisibility() {
    if (document.hidden) stop();
  }

  timer = window.setTimeout(() => {
    if (settled || document.hidden) return;
    stop();
    window.location.href = web;
  }, SCHEME_HANDOFF_MS);

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', stop);

  window.location.href = scheme;
}

/**
 * Hand a URL to the OS. A top-level navigation is what triggers Capacitor's
 * external-URL hand-off; `window.open` on a `geo:` URL just opens a dead tab,
 * so non-http schemes always go through `location.href`.
 */
function launch(url: string, scheme?: string | null): void {
  if (typeof window === 'undefined') return;
  if (scheme) {
    launchWithFallback(scheme, url);
    return;
  }
  if (isNative() || !url.startsWith('http')) {
    window.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Open the user's maps app with directions to a free-text destination. This is
 * what fires when they ask Mucka "give me directions to the next job".
 *
 * Given an address, we hand over its number, street and postcode and let the
 * maps app geocode that — never the whole address, whose house name or town
 * the maps app's search will happily match to somewhere else (see
 * `navigationQuery`). Given
 * a property's door pin, we send the coordinate, because the pin is where
 * somebody actually stood.
 */
export function openDirections(destination: MapsDestination, app?: NavApp): Promise<void> {
  if (isEmptyDestination(destination)) return Promise.resolve();
  const picked = coerce(app ?? resolveNavApp());
  const read = readDestination(destination);
  if (isWordsDestination(read)) {
    return launchThreeWords(read, c => ({ url: directionsUrl(picked, c), scheme: schemeUrl(picked, c, 'directions') }));
  }
  const dest = isCoord(read) ? read : (destination as string).trim();
  launch(directionsUrl(picked, dest), schemeUrl(picked, dest, 'directions'));
  return Promise.resolve();
}

/** Open the user's maps app showing a place (no turn-by-turn). */
export function openPlace(place: MapsDestination, app?: NavApp): Promise<void> {
  if (isEmptyDestination(place)) return Promise.resolve();
  const picked = coerce(app ?? resolveNavApp());
  const read = readDestination(place);
  if (isWordsDestination(read)) {
    return launchThreeWords(read, c => ({ url: placeUrl(picked, c), scheme: schemeUrl(picked, c, 'place') }));
  }
  const p = isCoord(read) ? read : (place as string).trim();
  launch(placeUrl(picked, p), schemeUrl(picked, p, 'place'));
  return Promise.resolve();
}

const LOCATE_TIMEOUT_MS = 6_000;

/**
 * Where three words point, asked at the tap and kept nowhere — not in memory
 * past this call, not in storage (licence clause 6.3(e)). Null on any failure,
 * offline included.
 */
async function locateThreeWords(words: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const res = await fetch(`/api/what3words/locate?words=${encodeURIComponent(words)}`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(LOCATE_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { lat?: unknown; lng?: unknown };
    return typeof body.lat === 'number' && typeof body.lng === 'number' && Number.isFinite(body.lat) && Number.isFinite(body.lng)
      ? { lat: body.lat, lng: body.lng }
      : null;
  } catch {
    return null;
  }
}

/**
 * Convert, then launch. When the words cannot be converted (offline, the
 * month's allowance spent) the engineer still gets somewhere precise:
 * what3words' own page for them, which navigates from there.
 */
async function launchThreeWords(
  dest: WordsDestination,
  build: (c: { lat: number; lng: number; label?: string | null }) => { url: string; scheme: string | null },
): Promise<void> {
  if (typeof window === 'undefined') return;
  // On the web, a tab opened after an await is a popup the browser blocks. So
  // take the tab NOW, while this still counts as the tap, and point it once
  // the words resolve. Native hands off by top-level navigation, which an
  // await does not affect.
  const tab = !isNative() ? window.open('', '_blank') : null;
  const c = await locateThreeWords(dest.words);
  const { url, scheme } = c ? build({ ...c, label: dest.label }) : { url: threeWordsUrl(dest.words), scheme: null };
  if (tab) {
    if (url.startsWith('http')) {
      tab.opener = null;
      tab.location.href = url;
      return;
    }
    tab.close();
  }
  launch(url, scheme);
}

export interface NavAppOption {
  id: NavApp;
  label: string;
  hint: string;
}

/**
 * The choices worth showing on this device. Android leads with its own default
 * because that IS the OS setting; iOS has no equivalent, so Apple Maps is just
 * the first of three.
 */
export function navAppOptions(): NavAppOption[] {
  const kind = deviceKind();
  if (kind === 'android') {
    return [
      { id: 'system', label: "My phone's default", hint: 'Opens whichever maps app Android is set to use' },
      { id: 'google', label: 'Google Maps', hint: 'Always start directions in Google Maps' },
      { id: 'waze', label: 'Waze', hint: 'Always start directions in Waze' },
    ];
  }
  if (kind === 'ios') {
    return [
      { id: 'apple', label: 'Apple Maps', hint: 'Built in to your iPhone' },
      { id: 'google', label: 'Google Maps', hint: 'Opens the app if you have it' },
      { id: 'waze', label: 'Waze', hint: 'Opens the app if you have it' },
    ];
  }
  return [
    { id: 'google', label: 'Google Maps', hint: 'Opens in a new tab' },
    { id: 'apple', label: 'Apple Maps', hint: 'Opens in a new tab' },
    { id: 'waze', label: 'Waze', hint: 'Opens in a new tab' },
  ];
}

/** Human label for a stored preference, for the settings row. */
export function navAppLabel(app: NavApp): string {
  return navAppOptions().find(o => o.id === app)?.label
    ?? (app === 'apple' ? 'Apple Maps' : app === 'waze' ? 'Waze' : app === 'google' ? 'Google Maps' : "My phone's default");
}
